import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Link, useNavigate } from "@/i18n/LocaleLink";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { NewsletterForm } from "@/components/NewsletterForm";
import { JOURNAL_IMAGES, IMAGES, useContent } from "@/data/content";
import { useSeo } from "@/hooks/useSeo";

// Journal bodies use *italic* and **bold** markers (as written in the source documents)
const renderRich = (text) =>
  text.split(/\*\*([^*]+)\*\*/g).map((chunk, i) =>
    i % 2 === 1 ? (
      <strong key={i}>{chunk}</strong>
    ) : (
      chunk.split(/\*([^*]+)\*/g).map((part, j) => (j % 2 === 1 ? <em key={`${i}-${j}`}>{part}</em> : part))
    )
  );

export default function JournalPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { ui, journal } = useContent();
  const post = journal.find((p) => p.slug === slug);
  useSeo({
    title: post ? `${post.title} | Women's Den` : ui.seo.journal.title,
    description: post ? post.excerpt : ui.seo.journal.description,
    image: JOURNAL_IMAGES[post ? post.slug : slug] || IMAGES.botanical,
  });
  useEffect(() => {
    if (!post) navigate("/journal", { replace: true });
  }, [post, navigate]);
  if (!post) return null;
  const others = journal.filter((p) => p.slug !== slug);
  const j = ui.journalPost;

  return (
    <main data-testid={`journal-post-${slug}`}>
      <PageHero eyebrow={`${post.threshold} · ${post.date} · ${post.readTime}`} title={post.title} image={JOURNAL_IMAGES[slug]} testid="journal-post-hero" />

      <section className="bg-cream py-24 lg:py-32">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <Link to="/journal" data-testid="journal-back-link"
              className="group inline-flex items-center gap-2 text-sm text-soft hover:text-clay transition-colors duration-300">
              <ArrowLeft size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:-translate-x-1" />
              {j.backToJournal}
            </Link>
          </Reveal>
          <article className="mt-12" data-testid="journal-article">
            {post.body.map((p, i) => (
              <Reveal key={i} delay={0.03 * i}>
                <p className={`mb-7 text-lg leading-relaxed text-ink/85 ${i === 0 ? "drop-cap" : ""}`}>{renderRich(p)}</p>
              </Reveal>
            ))}
            {post.signature && (
              <Reveal>
                <p className="mt-10 font-serif italic text-xl text-clay" data-testid="journal-signature">{post.signature}</p>
              </Reveal>
            )}
          </article>
          <Reveal>
            <div className="mt-16 border-t border-ink/10 pt-10">
              <p className="font-serif italic text-2xl text-ink">{j.enjoyed}</p>
              <div className="mt-6"><NewsletterForm /></div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-20">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase text-soft mb-6">{j.keepReading}</p>
            <div className="space-y-2">
              {others.map((o) => (
                <Link key={o.slug} to={`/journal/${o.slug}`} data-testid={`journal-related-${o.slug}`}
                  className="group flex items-center justify-between border-b border-ink/10 py-5">
                  <span className="font-serif text-xl text-ink group-hover:text-clay transition-colors duration-300">{o.title}</span>
                  <ArrowUpRight size={18} strokeWidth={1.25} className="text-clay transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
